import type { BoardConfig, MoveResolution, ResourceKey } from './game.js';

/**
 * Демоверсия «Твоё солнце таланта» (ТЗ демо, 12.10.2026).
 *
 * Демо — не отдельная игра, а та же партия на том же поле, ограниченная тремя
 * секторами. Всё, чем демо отличается от полной игры, собрано здесь: маршрут,
 * награда за подтверждённое задание, тексты сопровождения. Полная игра этот
 * модуль не читает.
 */

/**
 * Разновидность записи каталога.
 *
 * Полная игра и демо — две самостоятельные записи с независимыми правилами
 * доступа. Поле, колоды и сказки у них общие: демо ссылается на исходную игру
 * и своих копий не держит.
 */
export const GAME_VARIANTS = ['full', 'demo'] as const;
export type GameVariant = (typeof GAME_VARIANTS)[number];

/**
 * Сценарий демопартии — выбирается на каждую новую партию и после создания
 * не меняется.
 *
 * `solo` — «Хочу поиграть для себя»: один игрок, система ведёт его сама.
 * `facilitator` — «Хочу проводить игру с клиентами»: ведущий, комната, гости.
 */
export const DEMO_SCENARIOS = ['solo', 'facilitator'] as const;
export type DemoScenario = (typeof DEMO_SCENARIOS)[number];

export const DEMO_SCENARIO_LABELS: Record<DemoScenario, string> = {
  solo: 'Для себя',
  facilitator: 'Как ведущий',
};

/** Сектора демо — по порядку прохождения. Автор игры подтвердил эти три. */
export const DEMO_ROUTE_SECTOR_IDS = ['lighthouse', 'dragon', 'castle-key'] as const;

/**
 * Что даёт «Задание выполнено» в одиночном демо.
 *
 * По печатной инструкции: карта Дракона — «Проявленность», карта Замка и ключа
 * — «Поддержка». Маяку ресурс не положен: его результат — карта Таланта.
 * Награду определяет сервер по сектору карты; клиент её не передаёт.
 */
export const DEMO_SOLO_TASK_REWARDS: Readonly<Record<string, ResourceKey>> = {
  dragon: 'presence',
  'castle-key': 'support',
};

/* -------------------------------------------------------------------------- */
/* Маршрут                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Маршрут демо на настоящем поле.
 *
 * Строится по `sectorId` клеток, а не по счёту «первые двенадцать клеток»:
 * Маяк охватывает вершину кольца (клетки 22, 23, 0, 1), и фишка идёт от
 * стартовой клетки по ходу, пока не выйдет за третий сектор.
 */
export type DemoRoute = {
  sectorIds: string[];
  /** Индексы клеток поля в порядке хода, стартовая — первая. */
  cells: number[];
  /** Последняя клетка каждого сектора маршрута — там останавливается обрезанный бросок. */
  lastCellBySector: Record<string, number>;
  /** Последняя клетка третьего сектора: финиш демо. */
  finalCellIndex: number;
};

/**
 * Возвращает `null`, если поле не даёт пройти сектора подряд: такое поле —
 * ошибка конфигурации, и демо на нём запускать нельзя.
 */
export function buildDemoRoute(
  board: BoardConfig,
  sectorIds: readonly string[] = DEMO_ROUTE_SECTOR_IDS,
): DemoRoute | null {
  const total = board.cells.length;
  if (total === 0 || sectorIds.length === 0) return null;
  const start = Math.max(0, board.cells.findIndex((cell) => cell.id === board.startCellId));

  const cells: number[] = [];
  let order = 0;
  for (let step = 0; step < total; step += 1) {
    const index = (start + step) % total;
    const sectorId = board.cells[index]?.sectorId;
    const at = sectorId ? sectorIds.indexOf(sectorId) : -1;
    /* Вышли за маршрут или вернулись назад — маршрут кончился. */
    if (at < order) break;
    /* Перескок через сектор означает, что поле описывает другой порядок. */
    if (at > order + 1) return null;
    order = at;
    cells.push(index);
  }

  const lastCellBySector: Record<string, number> = {};
  for (const index of cells) {
    const sectorId = board.cells[index]!.sectorId!;
    lastCellBySector[sectorId] = index;
  }
  if (sectorIds.some((id) => lastCellBySector[id] === undefined)) return null;

  return {
    sectorIds: [...sectorIds],
    cells,
    lastCellBySector,
    finalCellIndex: cells[cells.length - 1]!,
  };
}

export type DemoMoveResolution = MoveResolution & {
  /** Сектор, на последней клетке которого остановился обрезанный бросок. */
  stoppedAtSectorId: string | null;
};

/**
 * Куда встаёт фишка в демо.
 *
 * Кубик честный — число выпадает любое, и в журнале остаётся выпавшее. Но
 * бросок не может перескочить следующий сектор целиком: фишка встаёт на его
 * последнюю клетку, лишние очки сгорают. В третьем секторе предел — последняя
 * клетка маршрута. Финиш здесь не ставится никогда: игрок финиширует, когда
 * закрыты все действия последней клетки, а не в момент броска.
 */
export function resolveDemoMove(
  board: BoardConfig,
  route: DemoRoute,
  from: number,
  dice: number,
): DemoMoveResolution {
  const offset = route.cells.indexOf(from);
  if (offset === -1) {
    /* Фишка вне маршрута — не двигаем её дальше, чтобы не уйти в закрытые сектора. */
    return { destination: from, finished: false, burnedPips: dice, stoppedAtSectorId: null };
  }
  const sectorId = board.cells[from]?.sectorId ?? route.sectorIds[0]!;
  const position = route.sectorIds.indexOf(sectorId);
  const limitSector = route.sectorIds[Math.min(position + 1, route.sectorIds.length - 1)]!;
  const limitOffset = route.cells.indexOf(route.lastCellBySector[limitSector]!);
  const raw = offset + dice;
  const reached = Math.min(raw, limitOffset);
  return {
    destination: route.cells[reached]!,
    finished: false,
    burnedPips: raw - reached,
    stoppedAtSectorId: raw > reached ? limitSector : null,
  };
}

/* -------------------------------------------------------------------------- */
/* Тексты сопровождения                                                        */
/* -------------------------------------------------------------------------- */

/*
  Все тексты демо — здесь, одним местом: поправить слово не должно означать
  правку компонентов или логики.

  Методические пояснения — ЧЕРНОВИК по согласованной концепции v0.3 (п. 1.7).
  До публикации их утверждает автор игры. Интерфейсные подсказки объясняют, что
  нажать; методические — смысл сектора. Они не дублируют друг друга.
*/
export const DEMO_TEXTS_STATUS: 'draft' | 'approved' = 'draft';

/** Карточка демо в каталоге и на странице игры. */
export const DEMO_CATALOG_COPY = {
  badge: 'Демоверсия',
  sectors: (open: number, total: number) => `${open} из ${total} секторов`,
  question: 'Как вы хотите познакомиться с игрой?',
  scenarios: {
    solo: {
      title: 'Хочу поиграть для себя',
      body:
        'Вы играете один. Система ведёт партию сама: показывает сказки, подсказывает следующий шаг ' +
        'и начисляет ресурсы после вашего подтверждения. Приглашать других нельзя.',
      action: 'Начать для себя',
    },
    facilitator: {
      title: 'Хочу проводить игру с клиентами',
      body:
        'Вы — ведущий. Создаёте комнату, приглашаете участников по ссылке, открываете сказки и ' +
        'принимаете ответы. Можно и самому участвовать как игрок: роль ведущего при этом остаётся.',
      action: 'Создать комнату ведущего',
    },
  },
} as const;

/**
 * Пояснение к сектору в два шага: `before` — пока карты сектора ещё нет (что
 * сейчас произойдёт и о чём сектор), `card` — когда карта получена и открыта
 * (вопросы к ней). Вопросы к карте до её появления сбивали бы с толку.
 */
export type DemoSectorGuidance = Readonly<Record<string, { before: string; card: string }>>;

/** Пояснения по сектору: игроку в одиночной партии и ведущему в групповой. */
export const DEMO_GUIDANCE: Readonly<
  Record<DemoScenario, { intro: string; sectors: DemoSectorGuidance; final: string }>
> = {
  solo: {
    intro:
      'Вы пройдёте три сектора настоящей игры: Маяк, Дракон, Замок и ключ. Отвечайте на вопросы ' +
      'карт вслух или запишите мысли для себя — платформе ответы не нужны.',
    sectors: {
      lighthouse: {
        before:
          'Маяк — знакомство с вашим талантом. Сейчас вы получите первую карту — талант от рождения. ' +
          'Когда откроете её, здесь появятся вопросы к ней.',
        card:
          'Маяк — ваш талант. Посмотрите на карту таланта. Что она значит для вас? Где этот талант уже проявляется?',
      },
      dragon: {
        before:
          'Дракон — страх, который может мешать проявлению таланта. Вытяните и откройте карту события — ' +
          'вопросы к ней появятся здесь.',
        card:
          'Дракон — страх проявления. Какие решения мог изменить этот страх? Что вы откладываете из-за него?',
      },
      'castle-key': {
        before:
          'Замок и ключ — роль-маска: как она помогает и чем может ограничивать. Вытяните и откройте ' +
          'карту роли — вопросы к ней появятся здесь.',
        card: 'Замок и ключ — роль-маска. Когда эта роль помогает вам, а когда ограничивает?',
      },
    },
    final:
      'Вы прошли путь «талант → страх → роль-маска». Что из этого вы хотите исследовать дальше?',
  },
  facilitator: {
    intro:
      'Вы ведёте ознакомительную партию: три сектора, настоящие карты. Пояснения для ведущего ' +
      'видите только вы — участникам они не показываются.',
    sectors: {
      lighthouse: {
        before:
          'Маяк — знакомство с талантом. Каждый участник получит талант от рождения; когда карта ' +
          'будет открыта, здесь появится подсказка к разговору о ней.',
        card: 'Предложите участнику назвать значение таланта и вспомнить пример из своей жизни.',
      },
      dragon: {
        before:
          'Дракон — страх, который может мешать проявлению таланта. Когда участник откроет карту ' +
          'события, здесь появится подсказка к разговору.',
        card:
          'Предложите связать событие карты с запросом участника. При затруднении он может обратиться за поддержкой группы. Обычно за ответ — «Проявленность».',
      },
      'castle-key': {
        before:
          'Замок и ключ — роль-маска: как она помогает и чем может ограничивать. Когда участник ' +
          'откроет карту роли, здесь появится подсказка к разговору.',
        card:
          'Помогите участнику исследовать роль без готовой трактовки. Важно увидеть и её защитную сторону. Обычно за ответ — «Поддержка».',
      },
    },
    final:
      'Вы провели ознакомительную партию. В полной игре — ещё три сектора и финальная «Печать Мастера».',
  },
};

/** Короткие описания секторов, закрытых в демо (концепция, п. 1.8). */
export const DEMO_CLOSED_SECTOR_DESCRIPTIONS: Readonly<Record<string, string>> = {
  bazaar: 'Как талант проявляется в реальных возможностях и ценности.',
  'sage-island': 'Как использовать накопленные таланты и ресурсы.',
  griffin: 'Как объединить таланты в личную формулу и завершить путешествие.',
};

/**
 * Сказки закрытых секторов — тексты Дарьи для итога демо (созвон 19.10.2026).
 *
 * Показываются под коротким описанием, когда человек выбирает сектор: та же
 * механика просмотра, без отдельной кнопки. Перенос строки — авторский, каждая
 * строка показывается отдельно; пустая строка — новый абзац.
 */
const LINE = '\n';
export const DEMO_CLOSED_SECTOR_TALES: Readonly<Record<string, string>> = {
  bazaar: [
    'Жизнь становится активной.',
    'Здесь всё происходит быстро.',
    'Возможности. Деньги. Риски. Рост.',
    'Вы пробуете. Ошибаетесь. Решаетесь.',
    'Договариваетесь. И начинаете чувствовать:',
    '«Мой талант может давать результат.»',
  ].join(LINE),
  'sage-island': [
    'Вы замедляетесь.',
    'Перед вами море.',
    'Где-то там — ответы.',
    'Вы добираетесь до острова…',
    'И слышите свой внутренний голос.',
    'Тихий. Но очень точный.',
    'Вы начинаете опираться на себя.',
  ].join(LINE),
  griffin: [
    'Вы поднимаетесь к вершине.',
    'Там вас ждёт Грифон — хранитель силы.',
    'Он смотрит на вас и словно спрашивает:',
    '«Ты уже готов признать, кто ты?»',
    'И вы собираете всё, что увидели о себе:',
    'свои таланты, свою уникальность, свою силу.',
    'И впервые называете это вслух.',
  ].join(LINE),
};

/** Что ведущий показал в групповой партии — для итога «Как ведущий». */
export const DEMO_FACILITATOR_FEATURES = {
  room: 'Комната и порядок хода',
  invite: 'Приглашение участников по ссылке',
  stories: 'Сказки секторов для всей группы',
  cards: 'Карты и задания секторов',
  resources: 'Приём ответа и выдача ресурсов',
} as const;
export type DemoFacilitatorFeature = keyof typeof DEMO_FACILITATOR_FEATURES;

/** Итоговый экран. */
export const DEMO_FINAL_COPY = {
  title: 'Итог путешествия',
  stages: {
    lighthouse: 'Талант',
    dragon: 'Страх',
    'castle-key': 'Роль-маска',
  } as Readonly<Record<string, string>>,
  missingCard: 'Карту этого сектора получить не успели.',
  closedSectorsTitle: 'Что ждёт в полной игре',
  /* В окне итога поля не видно — сектора выбираются кнопками ниже. */
  closedSectorsHint: 'Выберите сектор ниже, чтобы прочитать, о чём он. На поле эти сектора подсвечены.',
  closedSectorsHintBoard: 'Выберите сектор на поле или кнопкой ниже, чтобы прочитать, о чём он.',
  goToFinal: 'Перейти к итогу',
  birthTalent: 'Талант от рождения',
  cellTalent: 'Талант с клетки «Т»',
  rewardTalent: 'Талант за ответ',
  zoomHint: 'Нажмите на карту, чтобы рассмотреть её крупно.',
  ctaSolo: 'Открыть полную игру',
  ctaFacilitator: 'Проводить полную игру',
  ctaOwned: 'Перейти к полной игре',
  noOffer: 'Предложение полной версии появится здесь позже.',
  feedbackTitle: 'Два коротких вопроса',
  feedbackHint: 'По желанию. Ответы помогут сделать демо понятнее.',
  feedbackSaved: 'Спасибо, ответ сохранён.',
  /* Заголовок второй половины окна итога: краткий итог и действия — выше неё. */
  detailsTitle: 'Подробности пути',
  close: 'Закрыть итог',
  reopen: 'Итог путешествия',
  earlyFinish: 'Партия завершена раньше маршрута — показано то, что успели получить.',
  rayFull: 'Все шесть ячеек луча заполнены. Знакомство продолжается до конца сектора «Замок и ключ».',
} as const;

/**
 * Контекстные подсказки интерфейса — показываются, когда действие впервые
 * становится нужным, и объясняют, что нажать. Смысл сектора объясняют
 * методические пояснения (`DEMO_GUIDANCE`), не эти тексты.
 */
export const DEMO_COACH_COPY: Readonly<Record<string, { title: string; text: string }>> = {
  board: {
    title: 'Игровое поле',
    text: 'Это настоящее поле игры. В демо открыты три сектора: Маяк, Дракон и Замок и ключ. Фишка идёт по кругу от Маяка.',
  },
  story: {
    title: 'Сказка сектора',
    text: 'Каждый сектор начинается со сказки. Её нужно прочитать, прежде чем продолжить ход.',
  },
  start_talent: {
    title: 'Талант от рождения',
    text: 'Первая карта — ваш талант. Вытяните её здесь, затем откройте.',
  },
  card: {
    title: 'Карта клетки',
    text: 'Клетка даёт карту. Вытяните её и откройте — карта появится крупно, а потом останется в «Картах партии».',
  },
  task: {
    title: 'Задание выполнено',
    text: 'Ответьте на вопросы карты вслух или для себя. Кнопка отмечает выполнение и начисляет ресурс по правилам сектора — ответ при этом никуда не отправляется.',
  },
  dice: {
    title: 'Кубик',
    text: 'Бросьте кубик — фишка переместится на поле. Число выпадает случайно, но в демоверсии следующий сектор пропустить нельзя.',
  },
  resources: {
    title: 'Ресурсы',
    text: 'Полученные ресурсы ложатся на ваш луч к Солнцу и видны в карточке игрока.',
  },
  invite: {
    title: 'Приглашение участников',
    text: 'Ссылка-приглашение — в кнопке «Пригласить». Участники входят без регистрации. Можно и самому участвовать как игрок — вы останетесь ведущим.',
  },
  host_task: {
    title: 'Ответ участника',
    text: 'Когда участник ответил на вопросы карты, нажмите «Ответ принят» и выберите ресурс. До этого следующий бросок закрыт.',
  },
};
